import { useLoading } from "@agney/react-loading";
import ThreeDots from "@agney/react-loading/src/svg-loaders/three-dots.svg?react";
import { VisibilityOff } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Stack,
  Typography,
  styled,
  useTheme
} from "@mui/material";
import { Suggestion20Px, Inventory20Px } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useResizeDetector } from "react-resize-detector";

import { PortOrientation } from "~/api/portOrientation/portOrientation";
import { useAppSelector } from "~/app/store";
import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { useFlag } from "~/config/flags";
import { CompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import { AutostoreBinBorder } from "./AutostoreBinBorder";
import { AutostoreBinNumber } from "./AutostoreBinNumber";
import { BinAnimationOverlay } from "./BinAnimationOverlay";
import { BinState, getIsBinAnimationCategory } from "./BinState";
import { orientCompartments } from "./orientCompartments";

const AutostoreBinGridStyled = styled(Box)(({ theme: { spacing } }) => ({
  display: "grid",
  alignItems: "stretch",
  flexGrow: 1,
  gap: spacing(0.5),
  position: "relative"
}));

const AutostoreBinSkeletonStyled = styled(Stack, {
  shouldForwardProp: (propName: string) =>
    !["isShortSideBin", "enableColorChanges"].includes(propName)
})<{
  isShortSideBin: boolean;
  enableColorChanges: boolean;
}>(({ theme: { palette, spacing }, isShortSideBin, enableColorChanges }) => {
  return {
    WebkitMask:
      "radial-gradient(circle at top left, transparent 0, transparent 15px, black 0px) top left, " +
      "radial-gradient(circle at top right, transparent 0, transparent 15px, black 0px) top right, " +
      "radial-gradient(circle at bottom right, transparent 0, transparent 15px, black 0px) bottom right, " +
      "radial-gradient(circle at bottom left, transparent 0, transparent 15px, black 0px) bottom left",
    WebkitMaskSize: "55% 55%",
    WebkitMaskRepeat: "no-repeat",
    backgroundColor: enableColorChanges
      ? palette.autostoreDesignSystem.surface.interactive.muted.default
      : palette.darkGray.light,
    padding: isShortSideBin
      ? `${spacing(2)} ${spacing(0)}`
      : `${spacing(0)} ${spacing(2)}`,
    gap: spacing(0.5),
    position: "relative",
    maxWidth: isShortSideBin ? "240px" : "400px",
    minWidth: isShortSideBin ? "120px" : "200px",
    maxHeight: isShortSideBin ? "300px" : "240px",
    minHeight: isShortSideBin ? "200px" : "120px",
    aspectRatio: isShortSideBin ? "4 / 5" : "5 / 3",
    height: isShortSideBin ? "100%" : "auto",
    width: isShortSideBin ? "auto" : "100%", // 100% fixes issue where center aligning with flex will shrink the bin
    boxSizing: "border-box",
    margin: "0 auto"
  };
});

const AutostoreBinCompartmentStyled = styled(ButtonBase, {
  shouldForwardProp: (propName: string) =>
    ![
      "selected",
      "empty",
      "isBinFlaggedOrInventoryOnHold",
      "highlight",
      "enableColorChanges",
      "isInactive"
    ].includes(propName)
})<{
  selected: boolean;
  empty: boolean;
  isBinFlaggedOrInventoryOnHold: boolean;
  highlight: boolean;
  enableColorChanges: boolean;
  isInactive: boolean;
}>(({
  theme: { palette, spacing },
  selected,
  empty,
  isBinFlaggedOrInventoryOnHold,
  highlight,
  enableColorChanges,
  isInactive
}) => {
  let backgroundImage = "";

  if (!enableColorChanges) {
    if (!empty)
      backgroundImage =
        "linear-gradient(135deg, #ffffff 33.33%, #d1d1d1 33.33%, #d1d1d1 50%, #ffffff 50%, #ffffff 83.33%, #d1d1d1 83.33%, #d1d1d1 100%)";
    if (!empty && selected)
      backgroundImage = `linear-gradient(135deg, ${palette.success.main} 33.33%, #d1d1d1 33.33%, #d1d1d1 50%, ${palette.success.main} 50%, ${palette.success.main} 83.33%, #d1d1d1 83.33%, #d1d1d1 100%)`;
  }

  const backgroundColor = (() => {
    if (isBinFlaggedOrInventoryOnHold) return palette.warning.main;
    if (!selected) {
      if (!enableColorChanges) return palette.darkGray.main;
      return empty
        ? palette.autostoreDesignSystem.colour.primary.asWarmGray600
        : palette.autostoreDesignSystem.surface.interactive.baseAlt.default;
    }
    if (!enableColorChanges) return palette.primary.light;
    if (isInactive) {
      return palette.autostoreDesignSystem.colour.primary.asWarmGray600;
    }
    return palette.autostoreDesignSystem.surface.interactive.accent.default;
  })();

  const highlightBorderColor = enableColorChanges
    ? palette.autostoreDesignSystem.border.onStandard.statusInformative
    : palette.primary.main;

  return {
    display: "flex",
    flexDirection: "column",
    backgroundColor,
    backgroundImage,
    backgroundSize: `${spacing(2)} ${spacing(2)}`,
    border: highlight && selected ? `3px solid ${highlightBorderColor}` : ""
  };
});

export type Props = {
  state: BinState;
  pickQuantity?: number | string;
  pickCompartment: number | null;
  numberOfRows: number;
  numberOfColumns: number;
  binId?: number;
  hideBinId?: boolean;
  /** Show the bin ID while in the "Waiting for Bin" state. Defaults to true. */
  showBinIdWhenWaiting?: boolean;
  /** A number 0 based representing one of the bin comparments */
  selectedCompartment?: number;
  showPickQtyWhileBinLoaderIsActive?: boolean;
  isCycleCountBlind?: boolean;
  setSelectedCompartmentCallback?: (compartment: number) => void;
  onBinClick?: (compartmentNumber?: number) => void;
  /** ONLY allow compartment highlighting on specific compartment click with inventory. */
  allowSelectingCompartmentsWithInventoryOnly?: boolean;
  activeTaskGroupCompartments?: number[];
  /** Allow compartment highlighting on overall bin click. */
  highlightActiveCompartment?: boolean;
  /** Numbers 1 based representing the bin compartments */
  compartmentOccupancy?: CompartmentOccupancy[];
  /** Will display all quantities in compartments if provided */
  compartmentNumberWithQuantities?: {
    autostoreCompartmentNumber: number | undefined;
    quantity: number | undefined;
  }[];
  isSelectedCompartmentInventoryOnHold?: boolean;
  isBinFlagged?: boolean;
  moveTextCompartment?: number;
  /** The number of bins above in the bin animation */
  binsAbove?: number;
  renderBinButton?: React.ReactNode;
  isShortSideBin?: boolean;
};

const AutostoreBinContent = (props: Props) => {
  const { t } = useTranslation();
  const { width, ref } = useResizeDetector();
  const { palette } = useTheme();
  const { enableColorChanges } = useFlag();

  const {
    state,
    pickQuantity,
    pickCompartment,
    numberOfRows,
    numberOfColumns,
    binId: binNumber,
    hideBinId,
    showBinIdWhenWaiting = true,
    selectedCompartment,
    showPickQtyWhileBinLoaderIsActive,
    isCycleCountBlind,
    setSelectedCompartmentCallback,
    onBinClick,
    allowSelectingCompartmentsWithInventoryOnly = false,
    activeTaskGroupCompartments,
    highlightActiveCompartment,
    compartmentOccupancy,
    isSelectedCompartmentInventoryOnHold,
    isBinFlagged,
    compartmentNumberWithQuantities,
    moveTextCompartment,
    binsAbove,
    renderBinButton,
    isShortSideBin
  } = props;

  const { containerProps, indicatorEl } = useLoading({
    loading: true,
    indicator: (
      <ThreeDots
        color={palette.darkGray.light}
        width={isShortSideBin ? "100px" : "150px"}
      />
    )
  });

  const workstation = useAppSelector(selectThisWorkstation);

  const orientedCompartments = orientCompartments(
    Array.from({ length: (numberOfRows ?? 1) * (numberOfColumns ?? 1) }).map(
      (_, c) => c + 1
    ),
    numberOfColumns,
    workstation?.orientation ?? PortOrientation.SOUTH
  );

  const isCompartmentEmpty = (compartmentNumber: number) => {
    return (
      !compartmentOccupancy ||
      !compartmentOccupancy.length ||
      compartmentOccupancy?.some(
        (co) =>
          co.autostoreCompartmentNumber === compartmentNumber &&
          co.isCompartmentEmpty
      )
    );
  };

  const getFontSize = (displayQuantity?: number | string) => {
    const displayQuantityCharacterLength = displayQuantity
      ? displayQuantity.toString().length
      : 0;
    const fontSize =
      displayQuantityCharacterLength === 1
        ? 7
        : 9 - displayQuantityCharacterLength - (isShortSideBin ? 1 : 0);
    return fontSize;
  };

  const handleBinClick = (compartment: number) => {
    if (onBinClick && !allowSelectingCompartmentsWithInventoryOnly) {
      onBinClick(compartment);
    } else if (
      // If 'allowSelectingCompartmentsWithInventoryOnly' is true, only
      // allow selecting a compartment if has inventory
      onBinClick &&
      !compartmentOccupancy?.some(
        (co) =>
          co.autostoreCompartmentNumber === compartment + 1 &&
          co.isCompartmentEmpty
      )
    ) {
      onBinClick(compartment);
    }
    return setSelectedCompartmentCallback &&
      compartmentOccupancy?.some(
        (co) =>
          co.autostoreCompartmentNumber === compartment + 1 &&
          co.isCompartmentEmpty
      )
      ? setSelectedCompartmentCallback(compartment)
      : null;
  };

  const isBinAnimationCategory = getIsBinAnimationCategory(state);

  const getBinLabel = () => {
    if (state === "Bin Opened") {
      return `bin-${binNumber}`;
    } else if (isBinAnimationCategory) {
      return `bin-animation-${state}`;
    } else {
      return `no-bin`;
    }
  };

  const pickingQuantityEl = (qty?: string | number) => {
    const fontSize = getFontSize(qty);
    const verticalColumnCount = isShortSideBin ? numberOfRows : numberOfColumns;
    const compartmentFontSize =
      state === "Waiting for Bin"
        ? `${fontSize}rem`
        : `${fontSize / verticalColumnCount}rem`;
    return qty && ["Waiting for Bin", "Bin Opened"].includes(state) ? (
      <Typography
        variant="h1"
        fontWeight={600}
        fontSize={compartmentFontSize}
        lineHeight={1}
        color={
          state === "Waiting for Bin"
            ? palette.darkGray.light
            : palette.darkGray.dark
        }
      >
        {qty}
      </Typography>
    ) : (
      <></>
    );
  };

  const moveTextEl = () => (
    <Stack direction={isShortSideBin ? "column" : "row"} alignItems="center">
      <Suggestion20Px
        fill={palette.primary.main}
        style={{
          height: "100%",
          width: "40px",
          marginLeft: isShortSideBin ? "0" : "16px"
        }}
      />
      <Typography lineHeight={1} mr={isShortSideBin ? "0" : 1} maxWidth="100px">
        {t("move inventory here")}
      </Typography>
    </Stack>
  );

  const isSelected = (compartmentNumber: number) =>
    activeTaskGroupCompartments?.includes(compartmentNumber) ||
    (selectedCompartment !== undefined &&
      compartmentNumber === selectedCompartment) ||
    (pickCompartment !== null && compartmentNumber === pickCompartment);

  return (
    <AutostoreBinSkeletonStyled
      ref={ref}
      sx={{
        opacity: state === "Inactive" ? (enableColorChanges ? 0.4 : 0.35) : 1
      }}
      role="figure"
      aria-label={getBinLabel()}
      isShortSideBin={isShortSideBin ?? false}
      enableColorChanges={enableColorChanges}
      direction={isShortSideBin ? "row" : "column"}
    >
      <AutostoreBinBorder rotate={isShortSideBin ?? false} />
      {isBinAnimationCategory ? (
        <BinAnimationOverlay
          animationCategory={state}
          parentWidth={width}
          binsAbove={binsAbove}
        />
      ) : state === "Waiting for Bin" ? (
        <Stack
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          position="relative"
          bgcolor={palette.background.default}
          role="status"
          aria-label="Waiting for bin to be loaded"
          {...containerProps}
        >
          {indicatorEl}
          {showPickQtyWhileBinLoaderIsActive && pickingQuantityEl(pickQuantity)}
          {showBinIdWhenWaiting && !hideBinId && !!binNumber && (
            <AutostoreBinNumber binNumber={binNumber} />
          )}
        </Stack>
      ) : (
        <AutostoreBinGridStyled
          gridTemplateColumns={`repeat(${isShortSideBin ? numberOfRows : numberOfColumns}, 1fr)`}
          gridTemplateRows={`repeat(${isShortSideBin ? numberOfColumns : numberOfRows}, 1fr)`}
        >
          {orientedCompartments.map((compartmentNumber) => {
            const compartmentIndex = compartmentNumber - 1;
            const isCompartmentSelected = isSelected(compartmentIndex);
            const compartmentShowsOverlay =
              (isSelectedCompartmentInventoryOnHold && isCompartmentSelected) ||
              moveTextCompartment === compartmentNumber ||
              !!compartmentNumberWithQuantities?.some(
                (compartment) =>
                  compartment.autostoreCompartmentNumber === compartmentNumber
              ) ||
              (((selectedCompartment !== undefined &&
                compartmentIndex === selectedCompartment) ||
                (pickCompartment !== null &&
                  compartmentIndex === pickCompartment)) &&
                (isCycleCountBlind || !!pickQuantity));
            const showInventoryPresenceIcon =
              enableColorChanges &&
              !isCompartmentEmpty(compartmentNumber) &&
              !compartmentShowsOverlay;

            return (
              <AutostoreBinCompartmentStyled
                key={compartmentNumber}
                onClick={() => handleBinClick(compartmentIndex)}
                selected={isCompartmentSelected}
                highlight={!!highlightActiveCompartment}
                empty={isCompartmentEmpty(compartmentNumber)}
                enableColorChanges={enableColorChanges}
                isInactive={state === "Inactive"}
                isBinFlaggedOrInventoryOnHold={
                  !!(
                    isBinFlagged ||
                    (isSelectedCompartmentInventoryOnHold &&
                      isCompartmentSelected)
                  )
                }
                aria-selected={isCompartmentSelected}
                aria-label={
                  pickCompartment
                    ? `pickCompartment-${compartmentIndex}`
                    : `compartment-${compartmentIndex}-${
                        isCompartmentEmpty(compartmentNumber)
                          ? "unoccupied"
                          : "occupied"
                      }`
                }
              >
                {showInventoryPresenceIcon && (
                  <Box
                    aria-hidden
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& path": {
                        fill: isCompartmentSelected
                          ? palette.common.white
                          : palette.autostoreDesignSystem.colour.primary
                              .asBlack800
                      }
                    }}
                  >
                    <Inventory20Px width={28} height={28} />
                  </Box>
                )}
                {isSelectedCompartmentInventoryOnHold &&
                  isCompartmentSelected && (
                    <Typography>{t("inventory hold")}</Typography>
                  )}
                {((selectedCompartment !== undefined &&
                  compartmentIndex === selectedCompartment) ||
                  (pickCompartment !== null &&
                    compartmentIndex === pickCompartment)) &&
                  (isCycleCountBlind ? (
                    <VisibilityOff fontSize="large" />
                  ) : (
                    pickingQuantityEl(pickQuantity)
                  ))}
                {compartmentNumberWithQuantities &&
                  pickingQuantityEl(
                    compartmentNumberWithQuantities.find(
                      (compartment) =>
                        compartment.autostoreCompartmentNumber ===
                        compartmentNumber
                    )?.quantity ?? ""
                  )}
                {moveTextCompartment === compartmentNumber && moveTextEl()}
              </AutostoreBinCompartmentStyled>
            );
          })}
          {!hideBinId && !!binNumber && (
            <AutostoreBinNumber binNumber={binNumber} />
          )}
          {renderBinButton && <>{renderBinButton}</>}
        </AutostoreBinGridStyled>
      )}
      <AutostoreBinBorder rotate={isShortSideBin ?? false} />
    </AutostoreBinSkeletonStyled>
  );
};

export const AutostoreBin = (props: Props) => (
  <ASDSThemeProvider>
    <AutostoreBinContent {...props} />
  </ASDSThemeProvider>
);
